import { logSwallowed } from '@/lib/errors';
import { useEffect, useRef, useState } from 'react';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';

const log = createLogger('useObjectLock');

export interface LockConflict {
  lockId: string;
  userName: string;
  userId: string;
  expiresAt: string;
}

export interface UseObjectLockResult {
  lockId: string | null;
  conflict: LockConflict | null;
  loading: boolean;
  acquiring: boolean;
  release: () => Promise<void>;
  forceRelease: (lockId: string) => Promise<void>;
  requestRelease: (lockId: string, message?: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useObjectLock(
  schema: string,
  table: string,
  rowId: string | undefined,
  enabled: boolean = true
): UseObjectLockResult {
  const [lockId, setLockId] = useState<string | null>(null);
  const [conflict, setConflict] = useState<LockConflict | null>(null);
  const [loading, setLoading] = useState(false);
  const [acquiring, setAcquiring] = useState(false);
  const lockIdRef = useRef<string | null>(null);

  lockIdRef.current = lockId;

  const acquire = async () => {
    if (!enabled || !rowId) return;
    setAcquiring(true);
    setConflict(null);
    try {
      const res = await getSDK().lock.acquireLock({
        tableSchema: schema,
        tableName: table,
        rowId,
        lockType: 'edit',
        sessionId: '',
        ttlSeconds: 900,
      });
      if (res.acquired) {
        setLockId(res.lockId);
      } else if (res.conflict) {
        setLockId(null);
        setConflict({
          lockId: res.conflict.id,
          userId: res.conflict.userId,
          userName: res.conflict.userName || res.conflict.userId || 'unknown',
          expiresAt: res.conflict.expiresAt,
        });
      }
    } catch (e) {
      log.error('acquire lock failed', e);
    } finally {
      setAcquiring(false);
    }
  };

  const release = async () => {
    const id = lockIdRef.current;
    if (!id) return;
    try {
      await getSDK().lock.releaseLock({ lockId: id });
      setLockId(null);
    } catch (e) {
      log.error('release lock failed', e);
    }
  };

  const forceRelease = async (id: string) => {
    try {
      await getSDK().lock.forceReleaseLock({ lockId: id });
      await acquire();
    } catch (e) {
      log.error('force release lock failed', e);
    }
  };

  const requestRelease = async (id: string, message?: string) => {
    try {
      await getSDK().lock.requestReleaseLock({ lockId: id, message: message || '' });
    } catch (e) {
      log.error('request release lock failed', e);
    }
  };

  useEffect(() => {
    if (!enabled || !rowId) {
      setLockId(null);
      setConflict(null);
      return;
    }
    setLoading(true);
    acquire().finally(() => setLoading(false));
    return () => {
      release().catch((e) => logSwallowed(e, 'hooks/useObjectLock.ts:107'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, table, rowId, enabled]);

  // Продление блокировки каждые 10 минут, чтобы не потерять при длительном редактировании
  useEffect(() => {
    if (!lockId || !enabled) return;
    const interval = setInterval(() => {
      acquire();
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [lockId, enabled]);

  return {
    lockId,
    conflict,
    loading,
    acquiring,
    release,
    forceRelease,
    requestRelease,
    refresh: acquire,
  };
}
